import { Button, Input, Slider } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { Controller } from 'react-hook-form';

import Icon from '../Icon/Icon';
import Stepper, { Step } from '../Stepper/Stepper';

import useCreateResource from './useRatingPopup';

import { Props } from './RatingPopup.model';
import { Score } from '../../Helpers/useScoreHelper';

import styles from './RatingPopup.module.scss';

const RatingPopup = ({
	firstName,
	imageUrl,
	lastName,
	onClose,
	userId
}: Props) => {
	const {
		control,
		errors,
		handleStepBack,
		handleStepSubmit,
		handleSubmit,
		onPopupClose,
		onSubmit,
		state: { activeStep, score },
	} = useCreateResource({ onClose, userId });
	const intl = useIntl();
	const { TextArea } = Input;

	const getStepTitle = (step: number, scoreLabel?: string) => {
		let id;

		if (scoreLabel) {
			id = `ratings.step${step}.title.${scoreLabel}`;
		} else {
			id = `ratings.step${step}.title`;
		}

		return id ? intl.formatMessage({ id }, { b: (...chunks) => <b><i>{chunks}</i></b>, br: <br />, firstName, } ) : '';
	}

	return (
		<div className={styles.popupContainer}>
		<div className={`${styles.wrapper} ${styles.topBorder}`}>
			{activeStep === -1 &&
			<div className={styles.introReview} >
				<div className={styles.header}>
					<span className={styles.closeIcon} onClick={onPopupClose.bind(null, onClose)}>
						<Icon name='times' color='black'/>
					</span>
					<div className={styles.imageContainer}><img src={imageUrl} alt="Profile"/></div>
				</div>
				<div className={`${styles.content} ${styles.scrollable}`}>
					<h1><FormattedMessage id="ratings.intro.title" values={{ b: (...chunks: any) => <b><i>{chunks}</i></b>, firstName, lastName }} /></h1>
					<div className={styles.info}><FormattedMessage id="ratings.intro.info" values={{ firstName }} /></div>
					<Button size="large" type="primary" onClick={handleStepSubmit}><FormattedMessage id="buttons.rateExperience" /></Button>
				</div>
			</div>
			}

			{activeStep > -1 && activeStep < 2 &&
			<Stepper
				nextLabel={intl.formatMessage({ id: 'buttons.continue' })}
				paddedFooter={true}
				progressColor="#797568"
				showSteps={false}
				step={activeStep}
				onFinish={handleSubmit(onSubmit)}
				onStepBack={handleStepBack}
				onStepSubmit={handleStepSubmit}
			>
				<Step
					className={`${styles.step} ${styles[score]}`}
					subtitle={intl.formatMessage({ id: 'ratings.step1.subtitle' })}
					title={getStepTitle(1)}
				>
					<div className={styles.stepContent}>
						<span className={styles.closeIcon} onClick={onPopupClose.bind(null, onClose)}>
							<Icon name='times' color='black'/>
						</span>
						<div className={styles.star}>
							<Icon className={`${score === Score.Substandard && styles.visible}`} name="bad-star" type="custom" />
							<Icon className={`${score === Score.Good && styles.visible}`} name="star" type="solid" />
							<Icon className={`${score === Score.Exceptional && styles.visible}`} name="stars" type="solid" />
						</div>
						<div className={styles.scoreSlider}>
							<label><FormattedMessage id={`ratings.score.${score}`} /></label>
							<>
								<Controller
									name="score"
									control={control}
									render={({ field }) => (
										<Slider
											{...field}
											defaultValue={1}
											min={1}
											max={3}
											style={{ maxWidth: '360px', width: '100%' }}
											tooltipVisible={false}
										/>
									)}
								/>
								<p><FormattedMessage id="ratings.step1.instruction"/></p>
							</>
						</div>
					</div>

				</Step>
				<Step
					className={`${styles.step} ${styles[score]}`}
					subtitle={intl.formatMessage({ id: 'ratings.step2.subtitle' })}
					title={getStepTitle(2, score)}
				>
					<div className={styles.stepContent}>
						<span className={styles.closeIcon} onClick={onPopupClose.bind(null, onClose)}>
							<Icon name='times' color='black'/>
						</span>
						<div className={styles.fieldWrapper}>
							<label className={styles.floatingLabel}>
								<FormattedMessage id="formLabels.message" />
							</label>
							<Controller
								name="feedback"
								control={control}
								render={({ field }) => (
									<TextArea
										{...field}
										autoSize={{maxRows: 6, minRows: 6}}
										className={errors.feedback && 'error'}
										placeholder={intl.formatMessage({ id: `formPlaceholders.feedback` })}
										rows={6}
									/>
								)}
							/>
							<p><FormattedMessage id="ratings.step2.instruction"/></p>
						</div>
					</div>
				</Step>
			</Stepper>
			}

			{activeStep === 2 &&
			<div className={styles.finishReview} >
				<div className={`${styles.content} ${styles.scrollable}`}>
					<span className={styles.closeIcon} onClick={onPopupClose.bind(null, onClose)}>
						<Icon name='times' color='black'/>
					</span>
					<Icon className={styles.icon} color="green" name="badge-check" type="light" />
					<h1><FormattedMessage id="ratings.thankYou.title" values={{ b: (...chunks: any) => <b><i>{chunks}</i></b>, firstName, lastName }} /></h1>
					<div className={styles.info}><FormattedMessage id={`ratings.thankYou.info.${score}`} values={{ firstName }} /></div>
					<Button type="link" onClick={onPopupClose.bind(null, onClose)}><FormattedMessage id="ratings.thankYou.link"/></Button>
				</div>
			</div>
			}
		</div>
		</div>
	);
}

export default RatingPopup;